import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook to manage tenant slug for URL routing
 * Returns the current tenant slug from URL params or fetches it from the user's tenant
 */
export function useTenantSlug() {
  const { tenantSlug: urlSlug } = useParams<{ tenantSlug?: string }>();
  const { tenantId } = useAuth();
  const [tenantSlug, setTenantSlug] = useState<string | null>(urlSlug || null);
  const [loading, setLoading] = useState(!urlSlug && !!tenantId);

  useEffect(() => {
    if (urlSlug) {
      setTenantSlug(urlSlug);
      setLoading(false);
      return;
    }

    if (!tenantId) {
      setTenantSlug(null);
      setLoading(false);
      return;
    }

    // Fetch tenant slug from database
    const fetchSlug = async () => {
      try {
        const { data } = await supabase
          .from('tenants')
          .select('slug')
          .eq('id', tenantId)
          .maybeSingle();
        
        setTenantSlug(data?.slug || null);
      } catch (error) {
        console.error('Error fetching tenant slug:', error);
        setTenantSlug(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSlug();
  }, [urlSlug, tenantId]);

  /**
   * Build a tenant-scoped path
   * If tenant slug is available, returns /:tenantSlug/path
   * Otherwise returns /path
   */
  const buildPath = (path: string) => {
    if (tenantSlug) {
      return `/${tenantSlug}${path.startsWith('/') ? path : '/' + path}`;
    }
    return path.startsWith('/') ? path : '/' + path;
  };

  return {
    tenantSlug,
    loading,
    buildPath,
  };
}
